import React, { useEffect, useState, useRef, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { useLottoPage, StrategyType, HistoryEntry, LottoDraw } from './LottoPage.hooks';
import styles from './LottoPage.module.css';
import {
  Button,
  Typography,
  Paper,
  Grid,
  Tooltip,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  IconButton,
  Chip,
  Pagination,
  CircularProgress,
  Box,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import WhatshotIcon from '@mui/icons-material/Whatshot';
import BalanceIcon from '@mui/icons-material/Balance';
import StarIcon from '@mui/icons-material/Star';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import ShuffleIcon from '@mui/icons-material/Shuffle';
import InfoIcon from '@mui/icons-material/Info';
import {
  getRank,
  compareWithHistory,
  ComparisonResult,
  analyzeLottoPattern
} from '../../utils/lotto';
import Adsense from '../Ads/Adsense';
import { logEvent } from '../../utils/ga';
import { Link } from 'react-router-dom';
import CalculateIcon from '@mui/icons-material/Calculate';
import ShareButton from "../ShareButton/ShareButton";
import HistoryResultDisplay from "./HistoryResultDisplay";
import LottoTicketSimulator from "./LottoTicketSimulator";
import LottoBall from "../LottoBall/LottoBall";

const strategyInfo: Record<StrategyType & string, { title: string; description: string; icon: React.ReactElement }> = {
  '자주 나오는 수 + 의외의 수': {
    title: '자주 나오는 수 + 의외의 수',
    description: '가장 자주 나온 번호(Hot) 15개 중 3개와, 상대적으로 덜 나온 번호(Wild) 중에서 3개를 무작위로 조합합니다.',
    icon: <WhatshotIcon />,
  },
  '골고루 조합': {
    title: '골고루 조합',
    description: '전체 번호를 빈도 순으로 상위(Hot), 중위(Medium), 하위(Cold) 그룹으로 나누고, 각 그룹에서 2개씩 공평하게 조합합니다.',
    icon: <BalanceIcon />,
  },
  '인기 + 연속수 조합': {
    title: '인기 + 연속수 조합',
    description: '가장 자주 나온 번호(Hot) 2개, 서로 이어지는 연속 번호 2개, 그리고 나머지 번호 중에서 2개를 무작위로 조합합니다.',
    icon: <StarIcon />,
  },
  '통계 패턴 필터링': {
    title: '통계 패턴 필터링',
    description: '과거 당첨 데이터의 통계(홀짝 비율, 총합 구간)와 유사한 패턴의 번호 조합을 생성합니다.',
    icon: <FilterAltIcon />,
  },
  '완전 무작위': {
    title: '완전 무작위',
    description: '어떠한 통계나 패턴도 고려하지 않고, 1부터 45까지의 숫자 중 무작위로 6개를 선택합니다.',
    icon: <ShuffleIcon />,
  },
  'null': {
    title: '',
    description: '',
    icon: <></>,
  },
};

const LottoPage: React.FC = () => {
  const {
    lottoData,
    isLoading,
    error,
    recommendedNumbers,
    history,
    paginatedHistory,
    strategy,
    isGenerating,
    generateNumbers,
    currentPage,
    pageCount,
    handlePageChange,
  } = useLottoPage();

  const [animatedNumbers, setAnimatedNumbers] = useState<number[]>([]);
  const [comparisonResult, setComparisonResult] = useState<ComparisonResult | null>(null);
  const [patternComment, setPatternComment] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedStrategyInfo, setSelectedStrategyInfo] = useState(strategyInfo['null']);

  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const winningNumbersMap = useMemo(() => {
    if (!lottoData) return new Map<number, LottoDraw>();
    return new Map(lottoData.map(draw => [draw.drawNumber, draw]));
  }, [lottoData]);

  const handleInfoClick = (info: { title: string; description: string; icon: React.ReactElement }) => {
    setSelectedStrategyInfo(info);
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  // 번호 생성이 완료될 때마다 애니메이션과 분석을 실행
  useEffect(() => {
    if (recommendedNumbers.length === 0) {
      // If no numbers, clear any ongoing animation and reset display
      if (intervalRef.current) clearInterval(intervalRef.current);
      setAnimatedNumbers([]);
      return;
    }

    // Clear any existing interval before starting a new one
    if (intervalRef.current) clearInterval(intervalRef.current);

    // Reset animated numbers for the new animation
    setAnimatedNumbers([]);

    let index = 0;
    intervalRef.current = setInterval(() => {
      if (index < recommendedNumbers.length) {
        setAnimatedNumbers((prev) => {
          // Ensure we don't add duplicates if state updates are batched
          if (prev.includes(recommendedNumbers[index])) {
            return prev;
          }
          return [...prev, recommendedNumbers[index]];
        });
        index++;
      } else {
        // All numbers animated, clear the interval
        if (intervalRef.current) clearInterval(intervalRef.current);

        if (lottoData) {
          const historyResult = compareWithHistory(recommendedNumbers, lottoData);
          setComparisonResult(historyResult);
          const patternResult = analyzeLottoPattern(recommendedNumbers);
          setPatternComment(patternResult);
        }
      }
    }, 300);

    // 컴포넌트 언마운트 시 인터벌 정리
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [recommendedNumbers, lottoData]);

  const handleGenerateClick = (strategyType: StrategyType) => {
    if (isGenerating) return;
    setAnimatedNumbers([]);
    setComparisonResult(null);
    setPatternComment(null);
    generateNumbers(strategyType);
    logEvent('Lotto', 'GenerateNumbers', strategyType);
  };

  const renderStrategyButton = (strategyType: StrategyType, className: string) => {
    if (!strategyType) return null;
    const info = strategyInfo[strategyType];

    const button = (
      <Button
        variant="contained"
        className={className}
        startIcon={info.icon}
        disabled={isGenerating || isLoading}
        onClick={() => handleGenerateClick(strategyType)}
      >
        {info.title}
      </Button>
    );

    return (
      <div className={styles.strategyButtonWrapper}>
        {isMobile ? (
          <>
            {button}
            <IconButton onClick={() => handleInfoClick(info)} size="small" className={styles.infoButton}>
              <InfoIcon fontSize="small" />
            </IconButton>
          </>
        ) : (
          <Tooltip title={info.description}>{button}</Tooltip>
        )}
      </div>
    );
  };

  return (
    <div className={styles.container}>
      <Helmet>
        <title>로또 번호 추천기 | Enzo's Toolkit</title>
        <meta
          name="description"
          content="다양한 전략으로 로또 번호를 추천받으세요. 자주 나오는 수, 골고루 조합, 인기 + 연속수 조합, 통계 패턴 필터링 등 다양한 로또 번호 생성 전략을 제공합니다."
        />
      </Helmet>

      {isLoading && (
        <Box className={styles.centeredContainer}>
          <CircularProgress />
          <Typography variant="h6" sx={{ mt: 2 }}>
            과거의 지혜를 불러오는 중입니다...
          </Typography>
        </Box>
      )}

      {error && (
        <Box className={styles.centeredContainer}>
          <Typography variant="h6" color="error">
            데이터를 불러오는 데 실패했습니다.
          </Typography>
          <Typography color="error">에러: {error}</Typography>
        </Box>
      )}

      {!isLoading && !error && (
        <Paper elevation={3} className={`${styles.paper} ${styles.fadeIn}`}>
          <div className={styles.headerContainer}>
            <div className={styles.headerText}>
              <Typography variant="h4" gutterBottom>
                로또 번호 추천기
              </Typography>
              <Typography variant="subtitle1" color="textSecondary" gutterBottom>
                원하는 조합 전략을 선택하여 행운의 번호를 받아보세요.
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                버튼을 눌러 번호를 생성하세요.
              </Typography>
            </div>
            <div className={styles.headerButtonContainer}>
              <Button
                component={Link}
                to="/lotto/tax-calculator"
                variant="outlined"
                startIcon={<CalculateIcon />}
              >
                세금 계산기
              </Button>
              <ShareButton />
            </div>
          </div>

          <div className={styles.buttonContainer}>
            {renderStrategyButton('자주 나오는 수 + 의외의 수', styles.hotWildButton)}
            {renderStrategyButton('골고루 조합', styles.balancedButton)}
            {renderStrategyButton('인기 + 연속수 조합', styles.patternButton)}
            {renderStrategyButton('통계 패턴 필터링', styles.patternFilteredButton)}
            {renderStrategyButton('완전 무작위', styles.randomButton)}
          </div>

          <div className={styles.recommendedSection}>
            <Typography variant="h6">
              {strategy !== 'null' ? `추천 번호 (${strategy})` : '추천 번호'}
            </Typography>

            <LottoTicketSimulator selectedNumbers={animatedNumbers} />

            {animatedNumbers.length === 6 && patternComment && (
              <Typography variant="body1" className={styles.patternComment}>
                {patternComment}
              </Typography>
            )}

            {animatedNumbers.length === 6 && comparisonResult && <HistoryResultDisplay result={comparisonResult} />}
          </div>

          <Adsense adClient="ca-pub-9359050149154028" adSlot="7116664921" />

          {history.length > 0 && (
            <div className={styles.historySection}>
              <Typography variant="h6">생성 이력:</Typography>
              <Grid container spacing={1}>
                {paginatedHistory.map((entry: HistoryEntry, index: number) => {
                  const winningDraw = winningNumbersMap.get(entry.drawNumber);
                  const rank = getRank(entry.numbers, winningDraw);

                  const getRankColor = (rank: string): "warning" | "primary" | "success" | "error" | "default" => {
                    if (rank === '1등') return 'warning';
                    if (rank === '2등' || rank === '3등') return 'primary';
                    if (rank === '4등' || rank === '5등') return 'success';
                    if (rank === '낙첨') return 'error';
                    return 'default';
                  };

                  return (
                    <Grid item xs={12} key={index}>
                      <Paper variant="outlined" className={styles.historyItem}>
                        <div className={styles.historyHeader}>
                          <Typography variant="body2" className={styles.historyDrawInfo}>
                            <strong>{entry.drawNumber}회차</strong>
                            <span className={styles.historyStrategy}>({entry.strategy})</span>
                          </Typography>
                          <Chip
                            label={rank}
                            color={getRankColor(rank)}
                            size="small"
                            className={styles.rankChip}
                          />
                        </div>
                        <div className={styles.historyNumbers}>
                          {entry.numbers.map((num) => (
                            <LottoBall key={num} number={num} size="small" />
                          ))}
                        </div>
                        {winningDraw && (
                          <div className={styles.winningNumbers}>
                            <Typography variant="caption" color="textSecondary">
                              당첨: {winningDraw.numbers.join(', ')} + {(winningDraw as any).bonusNumber}
                            </Typography>
                          </div>
                        )}
                      </Paper>
                    </Grid>
                  );
                })}
              </Grid>
              {pageCount > 1 && (
                <div className={styles.paginationContainer}>
                  <Pagination
                    count={pageCount}
                    page={currentPage}
                    onChange={handlePageChange}
                    color="primary"
                    size={isMobile ? "small" : "medium"}
                  />
                </div>
              )}
            </div>
          )}
        </Paper>
      )}

      <Dialog open={dialogOpen} onClose={handleDialogClose}>
        <DialogTitle>{selectedStrategyInfo.title}</DialogTitle>
        <DialogContent>
          <DialogContentText>{selectedStrategyInfo.description}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>닫기</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default LottoPage;
